import React from 'react'
import Helmet from 'react-helmet'
import { graphql, PageRendererProps } from 'gatsby'
import { Block, Content, Flex, FlexSpacer, Yoga } from 'gerami'
import {
  FaArrowLeft,
  FaClock,
  FaFacebook,
  FaLocationArrow,
  FaTelegram,
  FaTwitter,
  FaWhatsapp
} from 'react-icons/fa'
import {
  FacebookShareButton,
  TelegramShareButton,
  TwitterShareButton,
  WhatsappShareButton
} from 'react-share'

import { ScheduleByIdQuery } from '../../../graphql-types'
import useSiteMetadata from '../../shared/hooks/use-site-metadata/use-site-metadata'
import App from '../app'
import Layout from '../../shared/components/layout/layout'
import Anchor from '../../shared/components/anchor/anchor'

type ScheduleProps = PageRendererProps & {
  data: ScheduleByIdQuery
}

const Schedule: React.FC<ScheduleProps> = ({ location, data }) => {
  const { markdownRemark: schedule } = data

  const siteMetadata = useSiteMetadata()
  const url = `${siteMetadata.siteUrl}${location.pathname}`
  const appName = siteMetadata?.title

  const title = schedule?.frontmatter?.title!
  const happensOn = schedule?.frontmatter?.happensOn!
  const loc = schedule?.frontmatter?.location || ``
  const body = schedule?.html || ``
  const excerpt = schedule?.excerpt

  return (
    <App pageRendererProps={{ location }}>
      <Helmet titleTemplate="%s | Schedules">
        <title>{`${schedule?.frontmatter?.title || 'Loading...'}`}</title>
      </Helmet>

      <Layout>
        <Content transparent size="4XL" className="margin-top-big">
          <Block first className="font-S padding-bottom-none">
            <Flex>
              <span className="margin-vertical-auto">
                <Anchor to="/schedules">
                  <small>
                    <FaArrowLeft />
                  </small>
                  <span className="inline-block margin-left-normal">
                    Schedules
                  </span>
                </Anchor>
              </span>

              <FlexSpacer />

              <span className="margin-vertical-auto fg-blackish right">
                <small className="inline-block middle margin-small margin-right-normal">
                  Share:
                </small>

                <FacebookShareButton
                  className="margin-horizontal-normal font-L middle"
                  url={url}
                  quote={`${title} | Schedules – ${appName}\n\n${excerpt}`}
                  hashtag={
                    appName ? `#${appName.replace(/ /g, '')}` : undefined
                  }
                >
                  <FaFacebook />
                </FacebookShareButton>

                <TelegramShareButton
                  className="margin-horizontal-normal font-L middle"
                  url={url}
                  title={`${title} | Schedules – ${appName}: ${excerpt}`}
                >
                  <FaTelegram />
                </TelegramShareButton>

                <TwitterShareButton
                  className="margin-horizontal-normal font-L middle"
                  url={url}
                  title={`${title} | Schedules – ${appName}`}
                  hashtags={appName ? [appName.replace(/ /g, ``)] : undefined}
                >
                  <FaTwitter />
                </TwitterShareButton>

                <WhatsappShareButton
                  className="margin-horizontal-normal font-L middle"
                  url={url}
                  title={`${title} | Schedules – ${appName}: ${excerpt}`}
                >
                  <FaWhatsapp />
                </WhatsappShareButton>
              </span>
            </Flex>
          </Block>
        </Content>

        <Content transparent size="XL">
          <Block>
            <h1 className="h0 margin-top-very-big margin-bottom-small">
              {title}
            </h1>

            <Content className="margin-top-big">
              <Block>
                <Yoga maxCol={2}>
                  <div>
                    <h6 className="middle padding-vertical-normal  fg-blackish">
                      <small className="inline-block padding-right-normal">
                        <FaClock />
                      </small>
                      HAPPENS ON:
                    </h6>
                    <div className="middle">{happensOn}</div>
                  </div>

                  <div>
                    <h6 className="middle padding-vertical-normal fg-blackish">
                      <small className="inline-block padding-right-normal">
                        <FaLocationArrow />
                      </small>
                      LOCATION:
                    </h6>
                    <div className="middle">{loc}</div>
                  </div>
                </Yoga>
              </Block>
            </Content>
          </Block>

          {!body ? null : (
            <>
              <Block first className="padding-bottom-none">
                <hr />
              </Block>

              <Block last>
                <article dangerouslySetInnerHTML={{ __html: body }} />
              </Block>
            </>
          )}
        </Content>
      </Layout>
    </App>
  )
}

export default Schedule

export const pageQuery = graphql`
  query ScheduleById($id: String!) {
    markdownRemark(id: { eq: $id }) {
      id
      excerpt
      frontmatter {
        title
        happensOn
        location
      }
      html
      excerpt
    }
  }
`
